import { useCallback, useMemo } from 'react';
import { Stack, Group, Text, Button, Box, Divider, Menu } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { notifications } from '@mantine/notifications';
import { modals } from '@mantine/modals';
import { useNavigate } from '@tanstack/react-router';
import { IconDots, IconTrash, IconShare } from '@tabler/icons-react';
import { PlaylistSettingsForm } from './PlaylistSettingsForm';
import { TagRulesBuilder } from './TagRulesBuilder';
import { PlaylistPublishModal } from './PlaylistPublishModal';
import { PlaylistUtils } from '../PlaylistUtils';
import { usePlaylistMedia } from '../usePlaylistMedia';
import { useUpdateTagPlaylist, useDeletePlaylists } from '../usePlaylistMutations';
import {
  SmartPlaylistFormSchema,
  type SmartPlaylistFormValues,
} from '../schemas/PlaylistForm.schema';
import { useRefIdConflict } from '@/shared/hooks/useRefIdConflict';
import type { TagPlaylistView } from '@/lib/types/Playlist';

interface SmartPlaylistContentProps {
  playlist: TagPlaylistView;
}

export function SmartPlaylistContent({ playlist }: SmartPlaylistContentProps) {
  const navigate = useNavigate();
  const updateMutation = useUpdateTagPlaylist(playlist.listId);
  const deleteMutation = useDeletePlaylists();
  const [publishOpened, { open: openPublish, close: closePublish }] = useDisclosure(false);

  const defaultValues: SmartPlaylistFormValues = {
    ...PlaylistUtils.toSettingsFormData(playlist),
    ...PlaylistUtils.toTagContentFormData(playlist),
  };

  const {
    control,
    handleSubmit,
    reset,
    formState: { isDirty, isSubmitting },
  } = useForm<SmartPlaylistFormValues>({
    resolver: zodResolver(SmartPlaylistFormSchema),
    defaultValues,
  });

  // Live match count against the current (possibly unsaved) rules — sourced
  // from the media library matching the playlist's mediaType, cache-first.
  const { items, isLoading: mediaLoading } = usePlaylistMedia(playlist.mediaType);
  const noun = PlaylistUtils.getMediaNoun(playlist.mediaType);
  const watchedRules = useWatch({ control, name: 'tagRules' });
  const watchedLimit = useWatch({ control, name: 'limit' });

  const refIdValue = useWatch({ control, name: 'refId' });
  const { conflict, conflictMessage, isChecking } = useRefIdConflict({
    refId: refIdValue,
    originalRefId: playlist.refId ?? '',
    selfScope: 'list',
    selfId: playlist.listId,
  });
  const matchSummary = useMemo(() => {
    const count = PlaylistUtils.computeFormMatchCount(
      playlist.mediaType,
      watchedRules ?? [],
      watchedLimit ?? '',
      mediaLoading ? undefined : items
    );
    if (count == null) return null;
    return `Currently matches ~${count} ${count === 1 ? noun.singular : noun.plural}`;
  }, [playlist.mediaType, watchedRules, watchedLimit, items, mediaLoading, noun]);

  const onSubmit = useCallback(async (data: SmartPlaylistFormValues) => {
    // Belt-and-braces for Enter-key submits; the Save button is already
    // disabled while a conflict is shown. Server validation stays authoritative.
    if (conflict) {
      notifications.show({ title: 'Reference ID conflict', message: conflictMessage, color: 'orange' });
      return;
    }
    const settingsData = {
      title: data.title,
      descShort: data.descShort,
      refId: data.refId,
      mediaStateType: data.mediaStateType,
    };
    const contentData = {
      tagRules: data.tagRules,
      sortUIType: data.sortUIType,
      limit: data.limit,
    };
    const request = PlaylistUtils.buildTagUpdateRequest(settingsData, contentData);

    try {
      await updateMutation.mutateAsync(request);
      reset(data);
      notifications.show({ title: 'Saved', message: 'Playlist updated.', color: 'green' });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to save playlist';
      notifications.show({ title: 'Error', message, color: 'red' });
    }
  }, [updateMutation, reset, conflict, conflictMessage]);

  const onInvalid = useCallback((fieldErrors: Record<string, { message?: string } | undefined>) => {
    const firstError = Object.values(fieldErrors).find(e => e?.message);
    if (firstError?.message) {
      notifications.show({ title: 'Validation Error', message: firstError.message, color: 'orange' });
    }
  }, []);

  const handleDelete = useCallback(() => {
    modals.openConfirmModal({
      title: 'Delete Playlist',
      children: (
        <Text size="sm">
          Are you sure you want to delete "{playlist.title}"? This action cannot be undone.
        </Text>
      ),
      labels: { confirm: 'Delete', cancel: 'Cancel' },
      confirmProps: { color: 'red' },
      onConfirm: () => {
        deleteMutation.mutate([playlist.listId], {
          onSuccess: () => {
            notifications.show({ title: 'Deleted', message: `"${playlist.title}" deleted.`, color: 'green' });
            navigate({ to: '/playlists' });
          },
          onError: (err) => {
            notifications.show({ title: 'Error', message: err.message, color: 'red' });
          },
        });
      },
    });
  }, [playlist, deleteMutation, navigate]);

  return (
    <form onSubmit={handleSubmit(onSubmit, onInvalid as Parameters<typeof handleSubmit>[1])}>
      <Stack gap="md">
        {/* Sticky save bar */}
        <Box
          pos="sticky"
          top="calc(var(--app-shell-header-height, 60px) + var(--mantine-spacing-md))"
          bg="white"
          py="sm"
          style={{ zIndex: 10, borderBottom: '1px solid var(--mantine-color-gray-2)' }}
        >
          <Group justify="space-between">
            <Text fw={600} size="lg">{playlist.title}</Text>
            <Group gap="sm">
              {isDirty && (
                <Button
                  variant="default"
                  onClick={() => reset()}
                  disabled={isSubmitting}
                >
                  Discard
                </Button>
              )}
              <Button type="submit" disabled={!isDirty || conflict != null} loading={isSubmitting}>
                Save Changes
              </Button>
              <Button
                variant="light"
                leftSection={<IconShare size={16} />}
                onClick={openPublish}
                disabled={isDirty}
                title={isDirty ? 'Save changes before publishing' : undefined}
              >
                Publish
              </Button>
              <Menu>
                <Menu.Target>
                  <Button variant="subtle" color="gray" px="xs">
                    <IconDots size={16} />
                  </Button>
                </Menu.Target>
                <Menu.Dropdown>
                  <Menu.Item
                    color="red"
                    leftSection={<IconTrash size={14} />}
                    onClick={handleDelete}
                  >
                    Delete Playlist
                  </Menu.Item>
                </Menu.Dropdown>
              </Menu>
            </Group>
          </Group>
        </Box>

        <PlaylistSettingsForm control={control} refIdConflictError={conflictMessage} refIdChecking={isChecking} />

        <Divider />

        <TagRulesBuilder control={control} matchSummary={matchSummary} />
      </Stack>

      <PlaylistPublishModal
        opened={publishOpened}
        onClose={closePublish}
        playlist={playlist}
      />
    </form>
  );
}
